import React, { useCallback, useState } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Checkbox } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useSelector } from "react-redux";
import { debounce } from "../../../../../utils";
import "../ReportsDetailed.css";
import { BreadcrumbAdmin } from "../../../../UI/BreadcrumbAdmin";
import DateRangePickerComp from "../../../../UI/DateRangePicker";
import { Search } from "../../../../UI/Search/Search";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import { useLazyGetAdminEmployeeActivityQuery } from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import {
  selectCurrentUser,
  selectSelectedCompany,
} from "../../../../../redux-toolkit/features/auth/authSlice";
import { resolveActiveCompanyId } from "../../../../../utils/companyContextNavigation";
import { EmployeeActivityTable } from "./EmployeeActivityTable";
import {
  PENDING_EMPLOYEE_ACTIVITY_CHECK_DEFAULTS,
  PENDING_EMPLOYEE_ACTIVITY_FIELD_LIST,
  SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS,
  formatAvgBookCompletionDays,
} from "../../../../Super Admin/Super Admin Reports/pendingEmployeeActivityColumns";

const list = [
  { name: "rowNum", label: "#" },
  { name: "empName", label: "Employee Name" },
  { name: "email", label: "Email" },
  { name: "role", label: "Role" },
  { name: "orgBooks", label: "Org Books Completed" },
  { name: "pointsEarned", label: "Points Earned" },
  { name: "pointsRedeemed", label: "Points Redeemed" },
  { name: "balance", label: "Balance Points" },
  { name: "timeTrials", label: "Time Trials Completed" },
  { name: "status", label: "Status" },
  { name: "lastActive", label: "Last Active" },
  // NEW (pending approval) — toggle SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS to hide
  ...(SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS
    ? PENDING_EMPLOYEE_ACTIVITY_FIELD_LIST
    : []),
];

const padDateUnit = (value) => String(value).padStart(2, "0");

const toIsoDatePart = (date) => {
  const selectedDate = new Date(date);
  const year = selectedDate.getFullYear();
  const month = padDateUnit(selectedDate.getMonth() + 1);
  const day = padDateUnit(selectedDate.getDate());
  return `${year}-${month}-${day}`;
};

const toUtcDayStartISOString = (date) => `${toIsoDatePart(date)}T00:00:00.000Z`;

const toUtcDayEndISOString = (date) => `${toIsoDatePart(date)}T23:59:59.999Z`;

const formatLastActiveCsv = (value) => {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Never";
  }
};

const mapEmployeeActivityToCsvRows = (rows = []) =>
  rows.map((obj, i) => ({
    "#": i + 1,
    "Employee Name": [obj.fname, obj.lname].filter(Boolean).join(" "),
    Email: obj.email ?? "",
    Role: obj.roleLabel ?? "",
    "Org Books Completed": obj.orgBooksCompleted ?? 0,
    "Points Earned": obj.pointsEarned ?? 0,
    "Points Redeemed": obj.pointsRedeemed ?? 0,
    "Balance Points": obj.balancePoints ?? 0,
    "Time Trials Completed": obj.timeTrialsCompleted ?? 0,
    Status: obj.activityStatus ?? "Inactive",
    "Last Active": formatLastActiveCsv(obj.lastLogin),
    // NEW (pending approval)
    ...(SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS
      ? {
          "Last Book Completion Date (New)": formatLastActiveCsv(
            obj.lastBookCompletionDate
          ),
          "Last Approved Task Date (New)": formatLastActiveCsv(
            obj.lastApprovedTaskDate
          ),
          "Unseen Notifications Count (New)":
            obj.unseenNotificationsCount ?? 0,
          "Avg Book Completion Days (New)": formatAvgBookCompletionDays(
            obj.avgBookCompletionDays
          ),
          "Badges Earned (New)": obj.badgesEarnedCount ?? 0,
        }
      : {}),
  }));

const escapeCsvCell = (value) => {
  const str = String(value ?? "");
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const downloadCsvFile = (rows, filename) => {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) => headers.map((key) => escapeCsvCell(row[key])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    filename.endsWith(".csv") ? filename : `${filename}.csv`
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const EmployeeActivity = () => {
  const [select, setSelect] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState("asc");
  const [isExporting, setIsExporting] = useState(false);
  const [dateRange, setDateRange] = React.useState(null);
  const ref = React.useRef(null);
  const [fetchAllEmployeeActivity] = useLazyGetAdminEmployeeActivityQuery();
  const selectedCompany = useSelector(selectSelectedCompany);
  const user = useSelector(selectCurrentUser);
  const companyId = resolveActiveCompanyId(selectedCompany, user);

  const closePopup = () => {
    setSelect(false);
  };

  useOutsideClick(ref, closePopup);

  const [check, setCheck] = useState({
    rowNum: true,
    empName: true,
    email: true,
    role: true,
    orgBooks: true,
    pointsEarned: true,
    pointsRedeemed: true,
    balance: true,
    timeTrials: true,
    status: true,
    lastActive: true,
    // NEW (pending approval)
    ...(SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS
      ? PENDING_EMPLOYEE_ACTIVITY_CHECK_DEFAULTS
      : {}),
  });

  const base = [
    {
      _id: 1,
      link: "/admin/reports",
      label: "Reports",
    },
  ];

  const setDebouncedSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 500),
    []
  );

  const handleChange = (e) => {
    setCheck({ ...check, [e.target.name]: e.target.checked });
  };

  const handleExport = async () => {
    if (isExporting || !companyId) return;
    setIsExporting(true);
    try {
      const result = await fetchAllEmployeeActivity(
        {
          sortBy,
          type: sortType,
          query: searchQuery,
          startDate: dateRange?.[0],
          endDate: dateRange?.[1],
          exportAll: true,
          companyId,
        },
        { preferCacheValue: false }
      ).unwrap();
      downloadCsvFile(
        mapEmployeeActivityToCsvRows(result?.data ?? []),
        "Employee activity"
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container">
      <BreadcrumbAdmin base={base} page={"Employee Activity"} />
      <p className="reportsDetailed__subtitle">
        Employee report for the selected company with role, org books completed,
        points earned and redeemed, balance, time trials, and last login. Active
        means at least one org or company book completed in the last two months.
      </p>
      <div className="reportsDetailed__head">
        <Search
          mini
          setSearchVal={(val) => setDebouncedSearchQuery(val)}
          placehold={"Search by name, email..."}
        />
        <div className="reportsDetailed__headright">
          <DateRangePickerComp
            onChange={(value) => {
              if (!value) {
                setDateRange(null);
              } else {
                const [start, end] = value;
                if (!start || !end) {
                  setDateRange(null);
                  return;
                }
                setDateRange([
                  toUtcDayStartISOString(start),
                  toUtcDayEndISOString(end),
                ]);
              }
            }}
          />
          <div ref={ref} className="reports__selectLayout">
            <Button
              variant="outlined"
              endIcon={
                <ArrowForwardIosIcon
                  sx={{
                    transform: select ? "rotate(-90deg)" : "rotate(90deg)",
                    color: "grey",
                    fontSize: "16px !important",
                  }}
                />
              }
              sx={{
                color: "var(--grey400)",
                borderColor: "var(--grey300)",
                width: "150px",
                fontWeight: "400 !important",
              }}
              onClick={() => setSelect(!select)}
            >
              Manage Field
            </Button>
            <div
              className={select ? "reports__selectActive" : "reports__select"}
            >
              <div className="reports__selectcontent">
                {list.map((item, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={Boolean(check[item.name])}
                        name={item.name}
                        onChange={handleChange}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "20px" } }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "15px" }}>{item.label}</span>
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <Button
            variant="contained"
            endIcon={<FileUploadOutlinedIcon sx={{ color: "white" }} />}
            onClick={handleExport}
            disabled={isExporting || !companyId}
          >
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>

      <div className="reports__table">
        <EmployeeActivityTable
          key={companyId || "no-company"}
          check={check}
          searchQuery={searchQuery}
          startDate={dateRange && dateRange[0]}
          endDate={dateRange && dateRange[1]}
          sortBy={sortBy}
          sortType={sortType}
          setSortBy={setSortBy}
          setSortType={setSortType}
        />
      </div>
    </div>
  );
};
